import React, { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  ModalBody,
  Modal,
  Form,
  Alert,
} from "reactstrap";
import Select from "react-select";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { useSelector, useDispatch } from "react-redux";
import { fetchJobsRequest, deleteJobRequest, updateJobRequest } from "../../../../store/job/action";
import { toast, ToastContainer } from "react-toastify";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import classnames from "classnames";
import confidooLogo from "../../../../assets/images/companies/confidoo.png";
import tagstreamLogo from "../../../../assets/images/companies/tagstream.png";
import rfidtunisieLogo from "../../../../assets/images/companies/rfid.png";
import CountUp from "react-countup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useTranslation } from 'react-i18next';
import parse from "html-react-parser";

// Custom Styles (unchanged)
const customStyles = `
  .demo-card { border-radius: 8px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); }
  .demo-modal .modal-content { border-radius: 8px; border: none; }
  .demo-modal .modal-header { background: linear-gradient(135deg, #2a4966 0%, #0ab39c 100%); color: #ffffff; border-top-left-radius: 8px; border-top-right-radius: 8px; padding: 1.5rem; }
  .demo-modal .modal-header h5 { font-weight: 600; font-size: 1.4rem; color: #ffffff; }
  .demo-modal .modal-body { padding: 1.5rem; background-color: #f9fafb; }
  .demo-modal .modal-body p { color: #333; font-size: 1rem; line-height: 1.6; }
  .demo-modal .modal-body strong { color: #2a4966; }
  .demo-table th, .demo-table td { vertical-align: middle; padding: 0.75rem; }
  .demo-table tr:hover { background-color: #f1f5f7; }
  .demo-widget-card { transition: transform 0.3s ease; }
  .demo-widget-card:hover { transform: translateY(-5px); }
  .filter-container { display: flex; flex-direction: row; gap: 1rem; align-items: center; flex-wrap: nowrap; overflow-x: auto; margin-bottom: 1rem; padding-bottom: 0.5rem; }
  .filter-container .form-control, .filter-container .form-select { border-radius: 4px; border: 1px solid #ced4da; min-width: 150px; font-size: 0.9rem; padding: 0.5rem 1rem; }
  .filter-container .form-control:focus, .filter-container .form-select:focus { border-color: #0ab39c; box-shadow: 0 0 0 0.2rem rgba(10, 179, 156, 0.25); }
  .action-btn { font-size: 0.85rem; padding: 0.4rem 0.8rem; transition: background-color 0.3s ease, transform 0.2s ease; }
  .action-btn:hover { transform: translateY(-2px); }
  .new-badge { margin-left: 0.5rem; font-size: 0.75rem; vertical-align: middle; }
  .card-header { background-color: transparent; border-bottom: 1px solid #e9ecef; padding: 1.5rem; }
  .card-title { font-size: 1.25rem; font-weight: 600; color: #2a4966; }
  .dropdown-menu { border-radius: 4px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); border: none; }
  .dropdown-item { font-size: 0.85rem; padding: 0.5rem 1rem; color: #2a4966; }
  .dropdown-item:hover { background-color: #f1f5f7; color: #0ab39c; }
  .dropdown-item i { margin-right: 0.5rem; }
  .pagination-block { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem; }
  .pagination-block .page-link { color: #2a4966; background-color: #fff; border: 1px solid #dee2e6; padding: 0.5rem 1rem; text-decoration: none; border-radius: 4px; }
  .pagination-block .page-link:hover { background-color: #e9ecef; }
  .pagination-block .page-item.disabled .page-link { color: #6c757d; background-color: #e9ecef; cursor: not-allowed; }
  @media (max-width: 768px) { .filter-container { flex-wrap: wrap; gap: 0.5rem; } .filter-container .form-control, .filter-container .form-select { min-width: 120px; font-size: 0.85rem; } }
`;

// Function to truncate description
const truncateDescription = (description, maxLength = 150) => {
  if (!description) return "";
  // Parse HTML to plain text for truncation
  const plainText = description.replace(/<[^>]+>/g, '');
  if (plainText.length <= maxLength) return description;

  // Find the last space before maxLength
  let truncated = plainText.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > 0) {
    truncated = truncated.substring(0, lastSpace);
  }

  // Reconstruct HTML with truncated text
  return parse(`${truncated}...`);
};

const JobList = () => {
  const { t } = useTranslation();
  document.title = t("job_list.title");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jobs, status, error } = useSelector((state) => state.Jobs);
  const { user } = useSelector((state) => ({
    user: state.Login.user,
  }));
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [companyFilter, setCompanyFilter] = useState("All");
  const [candidateCountFilter, setCandidateCountFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("desc");
  const [deleteModal, setDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [jobToEdit, setJobToEdit] = useState(null);
  const [description, setDescription] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const JobType = [
    {
      options: [
        { value: "All", label: t("job_list.filter.job_type.all") },
        { value: "Full-Time", label: t("job_list.filter.job_type.full_time") },
        { value: "Part-Time", label: t("job_list.filter.job_type.part_time") },
        { value: "Internship", label: t("job_list.filter.job_type.internship") },
        { value: "Contract", label: t("job_list.filter.job_type.contract") },
      ],
    },
  ];

  const SingleOptions = [
    {
      options: [
        { value: "Accounting & Finance", label: t("job_list.filter.category.accounting_finance") },
        { value: "Purchasing Manager", label: t("job_list.filter.category.purchasing_manager") },
        { value: "Education & Training", label: t("job_list.filter.category.education_training") },
        { value: "Marketing & Advertising", label: t("job_list.filter.category.marketing_advertising") },
        { value: "IT / Software Jobs", label: t("job_list.filter.category.it_software") },
        { value: "Digital Marketing", label: t("job_list.filter.category.digital_marketing") },
        { value: "Administrative Officer", label: t("job_list.filter.category.administrative_officer") },
        { value: "Government Jobs", label: t("job_list.filter.category.government_jobs") },
      ],
    },
  ];

  const CompanyOptions = [
    {
      options: [
        { value: "All", label: t("job_list.filter.company.all") },
        { value: "Confidoo", label: t("job_list.filter.company.confidoo") },
        { value: "Tagstream", label: t("job_list.filter.company.tagstream") },
        { value: "RFIDTunisie", label: t("job_list.filter.company.rfidtunisie") },
      ],
    },
  ];

  const CandidateCountOptions = [
    {
      options: [
        { value: "All", label: t("job_list.filter.candidate_count.all") },
        { value: "0", label: t("job_list.filter.candidate_count.zero") },
        { value: "1-5", label: t("job_list.filter.candidate_count.one_to_five") },
        { value: "6-10", label: t("job_list.filter.candidate_count.six_to_ten") },
        { value: "10+", label: t("job_list.filter.candidate_count.ten_plus") },
      ],
    },
  ];

  useEffect(() => {
    dispatch(fetchJobsRequest({ dispatch, navigate }));
  }, [dispatch, navigate]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilter = (type) => {
    setTypeFilter(type);
    setCurrentPage(1);
  };

  const handleCompanyFilter = (company) => {
    setCompanyFilter(company);
    setCurrentPage(1);
  };

  const handleCandidateCountFilter = (range) => {
    setCandidateCountFilter(range);
    setCurrentPage(1);
  };

  const handleSortOrder = (order) => {
    setSortOrder(order);
    setCurrentPage(1);
  };

  const filteredJobs = useMemo(() => {
    let updatedJobs = [...jobs];
    if (searchQuery) {
      updatedJobs = updatedJobs.filter(
          (job) =>
              job.jobTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
              job.jobPosition.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (typeFilter !== "All") {
      updatedJobs = updatedJobs.filter((job) => job.jobType === typeFilter);
    }
    if (companyFilter !== "All") {
      updatedJobs = updatedJobs.filter((job) => job.company === companyFilter);
    }
    if (candidateCountFilter !== "All") {
      updatedJobs = updatedJobs.filter((job) => {
        const count = job.candidateCount || 0;
        if (candidateCountFilter === "0") return count === 0;
        if (candidateCountFilter === "1-5") return count >= 1 && count <= 5;
        if (candidateCountFilter === "6-10") return count >= 6 && count <= 10;
        if (candidateCountFilter === "10+") return count > 10;
        return true;
      });
    }
    updatedJobs.sort((a, b) => {
      const dateA = new Date(a.datePublication || Date.now());
      const dateB = new Date(b.datePublication || Date.now());
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });
    return updatedJobs;
  }, [jobs, searchQuery, typeFilter, companyFilter, candidateCountFilter, sortOrder]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const goToPreviousPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const goToNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);

  const toggleEditModal = useCallback(() => {
    setEditModal((prev) => !prev);
    if (!editModal && !jobToEdit) {
      setJobToEdit(null);
      setDescription("");
      validation.resetForm();
    } else if (!editModal) {
      setJobToEdit(null);
    }
  }, [editModal, jobToEdit]);

  const handleEditJob = (job) => {
    setJobToEdit(job);
    if (job) {
      validation.setValues({
        jobTitle: job.jobTitle || "",
        jobPosition: job.jobPosition || "",
        jobCategory: job.jobCategory || "",
        jobType: job.jobType || "",
        description: job.description || "",
        startSalary: job.startSalary || "",
        lastSalary: job.lastSalary || "",
        country: job.country || "",
        state: job.state || "",
        lastDateApply: job.lastDateApply || "",
        closeDate: job.closeDate || "",
        tags: job.tags ? job.tags.join(", ") : "",
        company: job.company || "Confidoo",
      });
      setDescription(job.description || "");
    }
    setEditModal(true);
  };

  const handleDeleteJob = (job) => {
    setJobToDelete(job);
    setDeleteModal(true);
  };

  const confirmDeleteJob = () => {
    if (jobToDelete) {
      dispatch(deleteJobRequest({ id: jobToDelete.id, dispatch, navigate }));
      setDeleteModal(false);
      toast.success(t("job_list.toasts.delete_success"));
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      jobTitle: "",
      jobPosition: "",
      jobCategory: "",
      jobType: "",
      description: "",
      startSalary: "",
      lastSalary: "",
      country: "",
      state: "",
      lastDateApply: "",
      closeDate: "",
      tags: "",
      company: "Confidoo",
    },
    validationSchema: Yup.object({
      jobTitle: Yup.string().required(t("job_list.form.job_title_required")),
      jobPosition: Yup.string().required(t("job_list.form.job_position_required")),
      jobCategory: Yup.string().required(t("job_list.form.job_category_required")),
      jobType: Yup.string()
          .oneOf(["Full-Time", "Part-Time", "Internship", "Contract"])
          .required(t("job_list.form.job_type_required")),
      description: Yup.string().required(t("job_list.form.description_required")),
      startSalary: Yup.number().min(0, t("job_list.form.salary_positive")).optional(),
      lastSalary: Yup.number().min(0, t("job_list.form.salary_positive")).optional(),
      country: Yup.string().required(t("job_list.form.country_required")),
      state: Yup.string().required(t("job_list.form.state_required")),
      lastDateApply: Yup.date().required(t("job_list.form.last_date_apply_required")),
      closeDate: Yup.date().required(t("job_list.form.close_date_required")),
      tags: Yup.string().optional(),
      company: Yup.string().required(t("job_list.form.company_required")),
    }),
    onSubmit: (values) => {
      if (jobToEdit) {
        const jobData = {
          id: jobToEdit.id,
          titre: values.jobTitle,
          position: values.jobPosition,
          type: values.jobType,
          category: values.jobCategory,
          startSalary: values.startSalary || undefined,
          lastSalary: values.lastSalary || undefined,
          country: values.country,
          state: values.state,
          tags: values.tags
              ? values.tags.split(",").map((tag) => tag.trim()).filter((tag) => tag)
              : [],
          lastDateApply: values.lastDateApply,
          closeDate: values.closeDate,
          description: description,
          company: values.company,
          createdBy: jobToEdit.createdBy || user?._id,
        };
        dispatch(updateJobRequest({ id: jobToEdit.id, data: jobData, dispatch, navigate }));
        toggleEditModal();
        toast.success(t("job_list.toasts.update_success"));
      }
    },
  });

  const toggleDropdown = (id) => {
    setDropdownOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Job Statistics Widgets
  const jobWidgets = useMemo(() => [
    {
      title: t("job_list.widgets.total_jobs"),
      counter: jobs.length,
      suffix: "",
      decimals: 0,
      percentage: "0%",
      percentageClass: "success",
      arrowIcon: "ri-arrow-up-line",
      icon: "ri-file-list-3-line",
    },
    {
      title: t("job_list.widgets.jobs_with_candidates"),
      counter: jobs.filter((job) => (job.candidateCount || 0) > 0).length,
      suffix: "",
      decimals: 0,
      percentage: "0%",
      percentageClass: "success",
      arrowIcon: "ri-arrow-up-line",
      icon: "ri-user-2-line",
    },
    {
      title: t("job_list.widgets.jobs_no_candidates"),
      counter: jobs.filter((job) => (job.candidateCount || 0) === 0).length,
      suffix: "",
      decimals: 0,
      percentage: "0%",
      percentageClass: "danger",
      arrowIcon: "ri-arrow-down-line",
      icon: "ri-user-unfollow-line",
    },
  ], [jobs, t]);

  return (
      <React.Fragment>
        <style>{customStyles}</style>
        <div className="page-content">
          <Container fluid>
            <BreadCrumb title={t("job_list.title")} pageTitle={t("job_list.page_title")} />
            <ToastContainer closeButton={false} />
            <DeleteModal
                show={deleteModal}
                onDeleteClick={confirmDeleteJob}
                onCloseClick={() => setDeleteModal(false)}
            />
            {/* Statistics Widgets */}
            <Row>
              {jobWidgets.map((item, key) => (
                  <Col xxl={4} sm={6} key={key}>
                    <Card className="demo-widget-card card-animate">
                      <CardBody>
                        <div className="d-flex justify-content-between">
                          <div>
                            <p className="fw-medium text-muted mb-0">{item.title}</p>
                            <h2 className="mt-4 ff-secondary fw-semibold">
                          <span className="counter-value">
                            <CountUp
                                start={0}
                                end={item.counter}
                                duration={3}
                                suffix={item.suffix}
                                decimals={item.decimals}
                            />
                          </span>
                            </h2>
                            <p className="mb-0 text-muted">
                          <span className={`badge bg-light text-${item.percentageClass} mb-0`}>
                            <i className={`${item.arrowIcon} align-middle`}></i> {item.percentage}
                          </span>{" "}
                              {t("job_list.widgets.vs_previous_month")}
                            </p>
                          </div>
                          <div>
                            <div className="avatar-sm flex-shrink-0">
                          <span className="avatar-title bg-soft-info text-info rounded-circle fs-4">
                            <i className={item.icon}></i>
                          </span>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
              ))}
            </Row>
            {/* Job List Table */}
            <Row>
              <Col lg={12}>
                <Card className="demo-card">
                  <CardHeader className="border-0">
                    <h5 className="card-title mb-0 flex-grow-1">{t("job_list.card_title")}</h5>
                    <div className="flex-shrink-0">
                      <div className="filter-container hstack gap-2 mb-2">
                        <Input
                            type="text"
                            placeholder={t("job_list.search_placeholder")}
                            value={searchQuery}
                            onChange={handleSearch}
                            className="form-control"
                        />
                        <Input
                            type="select"
                            className="form-select"
                            value={typeFilter}
                            onChange={(e) => handleTypeFilter(e.target.value)}
                        >
                          {JobType[0].options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                          ))}
                        </Input>
                        <Input
                            type="select"
                            className="form-select"
                            value={companyFilter}
                            onChange={(e) => handleCompanyFilter(e.target.value)}
                        >
                          {CompanyOptions[0].options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                          ))}
                        </Input>
                        <Input
                            type="select"
                            className="form-select"
                            value={candidateCountFilter}
                            onChange={(e) => handleCandidateCountFilter(e.target.value)}
                        >
                          {CandidateCountOptions[0].options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                          ))}
                        </Input>
                        <Input
                            type="select"
                            className="form-select"
                            value={sortOrder}
                            onChange={(e) => handleSortOrder(e.target.value)}
                        >
                          <option value="desc">{t("job_list.filter.sort.newest")}</option>
                          <option value="asc">{t("job_list.filter.sort.oldest")}</option>
                        </Input>
                      </div>
                      <div className="text-end">
                        <NavLink to="/apps-job-new" className="btn btn-primary">
                          {t("job_list.add_new_job")}
                        </NavLink>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    {status === "loading" ? (
                        <div className="text-center py-4">
                          <i className="ri-loader-2-line fs-24 text-primary spin"></i>
                        </div>
                    ) : status === "failed" ? (
                        <Alert color="danger">Error: {error}</Alert>
                    ) : currentJobs.length === 0 ? (
                        <div className="py-4 mt-4 text-center" id="noresult">
                          <lord-icon
                              src="https://cdn.lordicon.com/msoeawqm.json"
                              trigger="loop"
                              colors="primary:#405189,secondary:#0ab39c"
                              style={{ width: "72px", height: "72px" }}
                          ></lord-icon>
                          <h5 className="mt-4">{t("job_list.no_results")}</h5>
                        </div>
                    ) : (
                        currentJobs.map((item, key) => (
                            <Card className="joblist-card" key={key}>
                              <CardBody>
                                <div className="d-flex mb-4 align-items-center">
                                  <img
                                      src={
                                        item.company === "Confidoo"
                                            ? confidooLogo
                                            : item.company === "Tagstream"
                                                ? tagstreamLogo
                                                : item.company === "RFIDTunisie"
                                                    ? rfidtunisieLogo
                                                    : confidooLogo
                                      }
                                      alt={`Company logo for ${item.company}`}
                                      style={{ maxWidth: "70px" }}
                                      className="company-logo w-6 h-6 object-contain mr-1 rounded-md"
                                  />
                                  <div className="ms-3 flex-grow-1">
                                  <NavLink to="/apps-job-details" state={{ job: item }}>
                                      <h5 className="job-title">{item.jobTitle}</h5>
                                    </NavLink>
                                    <p className="company-name text-muted mb-0">
                                      {item.jobPosition}{" "}
                                      <span className="badge bg-info ms-2">
                                  {`Candidate Count: ${item.candidateCount || 0}`}
                                </span>
                                    </p>
                                  </div>
                                  <div>
                                    <UncontrolledDropdown>
                                      <DropdownToggle tag="a" role="button">
                                        <i className="ri-more-fill fs-17"></i>
                                      </DropdownToggle>
                                      <DropdownMenu>
                                        <button className="dropdown-item" onClick={() => handleEditJob(item)}>
                                          <i className="ri-pencil-line me-2 align-bottom text-muted"></i>
                                          {t("job_list.actions.edit")}
                                        </button>
                                        <DropdownItem onClick={() => handleDeleteJob(item)}>
                                          <i className="ri-delete-bin-5-line me-2 align-bottom text-muted"></i>
                                          {t("job_list.actions.delete")}
                                        </DropdownItem>
                                      </DropdownMenu>
                                    </UncontrolledDropdown>
                                  </div>
                                </div>
                                <p className="text-muted job-description">{truncateDescription(item.description)}</p>
                                <div>
                                  {item.tags &&
                                      item.tags.map((tag, idx) => (
                                          <span key={idx} className="badge badge-soft-primary me-1">
                                  {tag}
                                </span>
                                      ))}
                                </div>
                              </CardBody>
                              <CardHeader className="card-footer border-top-dashed">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                  <div>
                                    <i className="ri-briefcase-2-line align-bottom me-1"></i>
                                    <span className="job-type">{item.jobType}</span>
                                  </div>
                                  <div>
                                    <i className="ri-map-pin-2-line align-bottom me-1"></i>
                                    <span className="job-location">{item.country}, {item.state}</span>
                                  </div>
                                  <div>
                                    <i className="ri-time-line align-bottom me-1"></i>
                                    <span className="job-postdate">
                                {new Date(item.datePublication).toLocaleDateString()}
                              </span>
                                  </div>
                                  <div className="hstack gap-2">
                                    <NavLink
                                        to="/apps-job-details"
                                        state={{ job: item }}

                                        className="btn btn-primary viewjob-list"
                                    >
                                      {t("job_list.view_more")} <i className="ri-arrow-right-line align-bottom ms-1"></i>
                                    </NavLink>
                                    <NavLink
                                        to="/apps-job-candidate-lists"
                                        state={{ job: item }}
                                        className="btn btn-info"
                                    >
                                      {t("job_list.view_candidates")}
                                    </NavLink>
                                  </div>
                                </div>
                              </CardHeader>
                            </Card>
                        ))
                    )}
                    {totalPages > 1 && (
                        <Row className="g-0 justify-content-end mb-4" id="pagination-element">
                          <Col sm={6}>
                            <div className="pagination-block pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                              <div className="page-item">
                                <Link
                                    to=""
                                    className="page-link"
                                    id="page-prev"
                                    onClick={goToPreviousPage}
                                    disabled={currentPage === 1}
                                >
                                  {t("job_list.pagination.previous")}
                                </Link>
                              </div>
                              <span id="page-num" className="pagination">
`Page ${currentPage} of ${totalPages}`                          </span>
                              <div className="page-item">
                                <Link
                                    to=""
                                    className="page-link"
                                    id="page-next"
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                >
                                  {t("job_list.pagination.next")}
                                </Link>
                              </div>
                            </div>
                          </Col>
                        </Row>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Modal id="editJob" isOpen={editModal} toggle={toggleEditModal} centered className="demo-modal">
              <ModalBody>
                <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                >
                  <Row>
                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="job-title-Input" className="form-label">
                          {t("job_list.form.job_title")} <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            className="form-control"
                            id="job-title-Input"
                            placeholder={t("job_list.form.job_title_placeholder")}
                            name="jobTitle"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.jobTitle}
                            invalid={validation.touched.jobTitle && validation.errors.jobTitle}
                        />
                        {validation.touched.jobTitle && validation.errors.jobTitle && (
                            <div className="invalid-feedback">{validation.errors.jobTitle}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="job-position-Input" className="form-label">
                          {t("job_list.form.job_position")} <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            className="form-control"
                            id="job-position-Input"
                            placeholder={t("job_list.form.job_position_placeholder")}
                            name="jobPosition"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.jobPosition}
                            invalid={validation.touched.jobPosition && validation.errors.jobPosition}
                        />
                        {validation.touched.jobPosition && validation.errors.jobPosition && (
                            <div className="invalid-feedback">{validation.errors.jobPosition}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="job-category-Input" className="form-label">
                          {t("job_list.form.job_category")} <span className="text-danger">*</span>
                        </Label>
                        <Select
                            id="job-category-Input"
                            name="jobCategory"
                            options={SingleOptions}
                            onChange={(option) => validation.setFieldValue("jobCategory", option.value)}
                            onBlur={() => validation.setFieldTouched("jobCategory", true)}
                            value={SingleOptions[0].options.find(
                                (opt) => opt.value === validation.values.jobCategory
                            )}
                            className={
                              validation.touched.jobCategory && validation.errors.jobCategory ? "is-invalid" : ""
                            }
                        />
                        {validation.touched.jobCategory && validation.errors.jobCategory && (
                            <div className="invalid-feedback">{validation.errors.jobCategory}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="job-type-Input" className="form-label">
                          {t("job_list.form.job_type")} <span className="text-danger">*</span>
                        </Label>
                        <Select
                            id="job-type-Input"
                            name="jobType"
                            options={JobType}
                            onChange={(option) => validation.setFieldValue("jobType", option.value)}
                            onBlur={() => validation.setFieldTouched("jobType", true)}
                            value={JobType[0].options.find((opt) => opt.value === validation.values.jobType)}
                            className={validation.touched.jobType && validation.errors.jobType ? "is-invalid" : ""}
                        />
                        {validation.touched.jobType && validation.errors.jobType && (
                            <div className="invalid-feedback">{validation.errors.jobType}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="company-Input" className="form-label">
                          {t("job_list.form.company")} <span className="text-danger">*</span>
                        </Label>
                        <Select
                            id="company-Input"
                            name="company"
                            options={CompanyOptions[0].options}
                            onChange={(option) => validation.setFieldValue("company", option.value)}
                            onBlur={() => validation.setFieldTouched("company", true)}
                            value={CompanyOptions[0].options.find(
                                (opt) => opt.value === validation.values.company
                            )}
                            className={validation.touched.company && validation.errors.company ? "is-invalid" : ""}
                        />
                        {validation.touched.company && validation.errors.company && (
                            <div className="invalid-feedback">{validation.errors.company}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="description-field" className="form-label">
                          {t("job_list.form.description")} <span className="text-danger">*</span>
                        </Label>
                        <CKEditor
                            editor={ClassicEditor}
                            data={description}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setDescription(data);
                              validation.setFieldValue("description", data);
                            }}
                            onBlur={(event, editor) => {
                              validation.setFieldTouched("description", true);
                            }}
                            config={{
                              toolbar: [
                                "heading",
                                "|",
                                "bold",
                                "italic",
                                "link",
                                "bulletedList",
                                "numberedList",
                                "blockQuote",
                                "|",
                                "undo",
                                "redo",
                              ],
                            }}
                        />
                        {validation.touched.description && validation.errors.description && (
                            <div className="invalid-feedback d-block">{validation.errors.description}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="start-salary-Input" className="form-label">
                          {t("job_list.form.start_salary")}
                        </Label>
                        <Input
                            type="number"
                            className="form-control"
                            id="start-salary-Input"
                            placeholder={t("job_list.form.start_salary_placeholder")}
                            name="startSalary"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.startSalary}
                            invalid={validation.touched.startSalary && validation.errors.startSalary}
                        />
                        {validation.touched.startSalary && validation.errors.startSalary && (
                            <div className="invalid-feedback">{validation.errors.startSalary}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="last-salary-Input" className="form-label">
                          {t("job_list.form.last_salary")}
                        </Label>
                        <Input
                            type="number"
                            className="form-control"
                            id="last-salary-Input"
                            placeholder={t("job_list.form.last_salary_placeholder")}
                            name="lastSalary"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.lastSalary}
                            invalid={validation.touched.lastSalary && validation.errors.lastSalary}
                        />
                        {validation.touched.lastSalary && validation.errors.lastSalary && (
                            <div className="invalid-feedback">{validation.errors.lastSalary}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="country-Input" className="form-label">
                          {t("job_list.form.country")} <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            className="form-control"
                            id="country-Input"
                            placeholder={t("job_list.form.country_placeholder")}
                            name="country"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.country}
                            invalid={validation.touched.country && validation.errors.country}
                        />
                        {validation.touched.country && validation.errors.country && (
                            <div className="invalid-feedback">{validation.errors.country}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="state-Input" className="form-label">
                          {t("job_list.form.state")} <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            className="form-control"
                            id="state-Input"
                            placeholder={t("job_list.form.state_placeholder")}
                            name="state"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.state}
                            invalid={validation.touched.state && validation.errors.state}
                        />
                        {validation.touched.state && validation.errors.state && (
                            <div className="invalid-feedback">{validation.errors.state}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="last-apply-date-Input" className="form-label">
                          {t("job_list.form.last_date_apply")} <span className="text-danger">*</span>
                        </Label>
                        <Flatpickr
                            className="form-control"
                            id="last-apply-date-Input"
                            placeholder={t("job_list.form.date_placeholder")}
                            options={{
                              altInput: true,
                              altFormat: "F j, Y",
                              dateFormat: "Y-m-d",
                            }}
                            value={validation.values.lastDateApply}
                            onChange={([date]) => validation.setFieldValue("lastDateApply", date)}
                            onBlur={() => validation.setFieldTouched("lastDateApply", true)}
                        />
                        {validation.touched.lastDateApply && validation.errors.lastDateApply && (
                            <div className="invalid-feedback d-block">{validation.errors.lastDateApply}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="close-date-Input" className="form-label">
                          {t("job_list.form.close_date")} <span className="text-danger">*</span>
                        </Label>
                        <Flatpickr
                            className="form-control"
                            id="close-date-Input"
                            placeholder={t("job_list.form.date_placeholder")}
                            options={{
                              altInput: true,
                              altFormat: "F j, Y",
                              dateFormat: "Y-m-d",
                            }}
                            value={validation.values.closeDate}
                            onChange={([date]) => validation.setFieldValue("closeDate", date)}
                            onBlur={() => validation.setFieldTouched("closeDate", true)}
                        />
                        {validation.touched.closeDate && validation.errors.closeDate && (
                            <div className="invalid-feedback d-block">{validation.errors.closeDate}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="tags-field" className="form-label">
                          {t("job_list.form.tags")}
                        </Label>
                        <Input
                            type="text"
                            className="form-control"
                            id="tags-field"
                            placeholder={t("job_list.form.tags_placeholder")}
                            name="tags"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.tags}
                        />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="hstack gap-2 justify-content-end">
                        <Button type="button" className="btn btn-light" onClick={toggleEditModal}>
                          {t("job_list.form.cancel")}
                        </Button>
                        <Button type="submit" color="success">
                          {t("job_list.form.save")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </ModalBody>
            </Modal>
          </Container>
        </div>
      </React.Fragment>
  );
};

export default JobList;